"use server";

import { isLocale } from "@/i18n/config";
import { getProductView } from "@/lib/data/products";
import { nowInSeoul, addDaysISO, MIN_BOOKING_ADVANCE_DAYS } from "@/lib/availability";
import { prisma } from "@/lib/prisma";
import {
  createPendingReservation,
  completeReservationPayment,
  cancelPendingReservation,
  resolveStudioForProduct,
} from "@/lib/store/reservations";
import { verifyPortOnePayment } from "@/lib/portone";
import type { Locale } from "@/i18n/config";

export interface CreatePendingReservationPayload {
  locale: string;
  productId: string;
  dateISO: string;
  startMinute: number;
  guestsCount: number;
  couponCode?: string;
  studioId?: string;
  customer: {
    name: string;
    phone: string;
    email: string;
    country: string;
    language?: string;
    gender?: string;
    messengerType?: string;
    messengerId?: string;
    profileImageUrl?: string;
  };
  song: {
    songTitle: string;
    artistName: string;
    songUrl?: string;
  };
}

export type CreatePendingReservationResponse =
  | { ok: true; reservationNo: string; totalAmount: number }
  | { ok: false; error: "INVALID" | "SLOT_TAKEN" | "UNKNOWN" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export async function createPendingReservationAction(
  payload: CreatePendingReservationPayload,
): Promise<CreatePendingReservationResponse> {
  try {
    const locale: Locale = isLocale(payload.locale) ? payload.locale : "en";
    const product = await getProductView(payload.productId);
    if (!product) return { ok: false, error: "INVALID" };

    const resolved = await resolveStudioForProduct(product);
    if (!resolved) return { ok: false, error: "UNKNOWN" };

    let studioId = resolved.studioId;
    if (payload.studioId) {
      const chosen = await prisma.studio.findFirst({
        where: {
          id: payload.studioId,
          branchId: resolved.branchId,
          status: "ACTIVE",
          deletedAt: null,
        },
        select: { id: true },
      });
      if (chosen) studioId = chosen.id;
    }

    const { name, email, phone } = payload.customer;
    if (!name.trim() || !EMAIL_RE.test(email.trim()) || !phone?.trim()) {
      return { ok: false, error: "INVALID" };
    }
    if (!payload.song.songTitle.trim() || !payload.song.artistName.trim()) {
      return { ok: false, error: "INVALID" };
    }

    const today = nowInSeoul();
    const minDays =
      product.minAdvanceDays ??
      product.policy.minAdvanceDays ??
      MIN_BOOKING_ADVANCE_DAYS;
    let minDate = addDaysISO(today.dateISO, minDays);
    if (product.policy.validFrom) {
      const validFromDate = product.policy.validFrom.slice(0, 10);
      if (validFromDate > minDate) minDate = validFromDate;
    }

    const maxDays =
      product.maxAdvanceDays ?? product.policy.maxAdvanceDays ?? 90;
    let maxDate = addDaysISO(today.dateISO, maxDays);
    if (product.policy.validTo) {
      const validToDate = product.policy.validTo.slice(0, 10);
      if (validToDate < maxDate) maxDate = validToDate;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(payload.dateISO)) {
      return { ok: false, error: "INVALID" };
    }
    if (payload.dateISO < minDate || payload.dateISO > maxDate) {
      return { ok: false, error: "INVALID" };
    }

    if (
      !Number.isInteger(payload.guestsCount) ||
      payload.guestsCount < product.minGuests ||
      payload.guestsCount > product.maxGuests
    ) {
      return { ok: false, error: "INVALID" };
    }

    if (
      !Number.isInteger(payload.startMinute) ||
      payload.startMinute % 60 !== 0
    ) {
      return { ok: false, error: "INVALID" };
    }

    const result = await createPendingReservation({
      product,
      studioId,
      branchId: resolved.branchId,
      dateISO: payload.dateISO,
      startMinute: payload.startMinute,
      guestsCount: payload.guestsCount,
      couponCode: payload.couponCode,
      locale,
      customer: {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim().toLowerCase(),
        country: payload.customer.country || "OTHER",
        language: payload.customer.language || locale,
        gender: normalizeGender(payload.customer.gender),
        messengerType: normalizeMessenger(payload.customer.messengerType),
        messengerId: payload.customer.messengerId?.trim() || undefined,
        profileImageUrl: normalizeProfileImage(payload.customer.profileImageUrl),
      },
      song: {
        songTitle: payload.song.songTitle.trim(),
        artistName: payload.song.artistName.trim(),
        songUrl: payload.song.songUrl?.trim() || undefined,
      },
    });

    if (!result.ok) return { ok: false, error: result.error };
    return {
      ok: true,
      reservationNo: result.reservationNo,
      totalAmount: result.totalAmount,
    };
  } catch {
    return { ok: false, error: "UNKNOWN" };
  }
}

export interface CompleteReservationPaymentPayload {
  reservationNo: string;
  payment?: {
    paymentId: string;
    txId?: string;
    payMethod?: string;
    totalAmount?: number;
    payerName?: string;
    payerPhone?: string;
  };
}

export type CompleteReservationPaymentResponse =
  | { ok: true; reservationNo: string }
  | { ok: false; error: "INVALID" | "NOT_FOUND" | "UNKNOWN" };

export async function completeReservationPaymentAction(
  payload: CompleteReservationPaymentPayload,
): Promise<CompleteReservationPaymentResponse> {
  try {
    if (payload.payment?.paymentId) {
      const verify = await verifyPortOnePayment(payload.payment.paymentId);
      if (!verify.ok) {
        return { ok: false, error: "INVALID" };
      }
    }

    const result = await completeReservationPayment({
      reservationNo: payload.reservationNo,
      payment: payload.payment,
    });

    if (!result.ok) return { ok: false, error: result.error };
    return { ok: true, reservationNo: result.reservationNo };
  } catch {
    return { ok: false, error: "UNKNOWN" };
  }
}

export async function cancelPendingReservationAction(
  reservationNo: string,
): Promise<{ ok: boolean }> {
  try {
    return await cancelPendingReservation(reservationNo);
  } catch {
    return { ok: false };
  }
}

export interface SubmitReservationPayload {
  locale: string;
  productId: string;
  dateISO: string;
  startMinute: number;
  guestsCount: number;
  couponCode?: string;
  studioId?: string;
  payment?: {
    paymentId: string;
    txId?: string;
    payMethod?: string;
    totalAmount?: number;
    payerName?: string;
    payerPhone?: string;
  };
  customer: {
    name: string;
    phone: string;
    email: string;
    country: string;
    language?: string;
    gender?: string;
    messengerType?: string;
    messengerId?: string;
    profileImageUrl?: string;
  };
  song: {
    songTitle: string;
    artistName: string;
    songUrl?: string;
  };
}

export type SubmitReservationResponse =
  | { ok: true; reservationNo: string }
  | { ok: false; error: "INVALID" | "SLOT_TAKEN" | "UNKNOWN" };

export async function submitReservation(
  payload: SubmitReservationPayload,
): Promise<SubmitReservationResponse> {
  try {
    const pending = await createPendingReservationAction(payload);
    if (!pending.ok) return pending;

    const completed = await completeReservationPaymentAction({
      reservationNo: pending.reservationNo,
      payment: payload.payment,
    });
    if (!completed.ok) return { ok: false, error: "UNKNOWN" };

    return { ok: true, reservationNo: pending.reservationNo };
  } catch {
    return { ok: false, error: "UNKNOWN" };
  }
}

function normalizeGender(value?: string) {
  return value === "MALE" || value === "FEMALE" || value === "OTHER"
    ? value
    : undefined;
}

function normalizeMessenger(value?: string) {
  return ["KAKAO", "LINE", "WECHAT", "WHATSAPP", "TELEGRAM"].includes(value ?? "")
    ? (value as "KAKAO" | "LINE" | "WECHAT" | "WHATSAPP" | "TELEGRAM")
    : undefined;
}

/** Only locally uploaded asset paths are accepted */
function normalizeProfileImage(value?: string) {
  const trimmed = value?.trim() ?? "";
  if (trimmed.includes("..") || trimmed.includes("//")) return undefined;
  const match = /^\/(uploads|profile_uploads|profile-uploads)\/[A-Za-z0-9._\/-]+$/.exec(
    trimmed,
  );
  return match ? match[0] : undefined;
}
